import type { ReactNode } from 'react';
import type { IconName } from '../module';

/**
 * Chunky line icons in the sticker style: square-ish joins, heavy ink strokes, an optional flat
 * fill. 24 unit grid. Always pair with a visible label or `label` for screen readers.
 */
const PATHS: Record<IconName, ReactNode> = {
  home: <path d="M3 11.5 12 3.5l9 8V21h-6.5v-6h-5v6H3z" />,
  stats: (
    <>
      <path d="M3.5 21V13h4.5v8z" />
      <path d="M9.75 21V4h4.5v17z" />
      <path d="M16 21V9h4.5v12z" />
    </>
  ),
  ball: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3v18M3 12h18" fill="none" />
      <path d="M6 5.2c2.6 2 3.4 4.4 3.4 6.8s-.8 4.8-3.4 6.8M18 5.2c-2.6 2-3.4 4.4-3.4 6.8s.8 4.8 3.4 6.8" fill="none" />
    </>
  ),
  crew: (
    <>
      <circle cx="8.5" cy="8" r="3.5" />
      <circle cx="16.5" cy="9" r="3" />
      <path d="M2.5 20.5c0-3.6 2.7-6.5 6-6.5s6 2.9 6 6.5z" />
      <path d="M14.8 14.2c.6-.2 1.1-.2 1.7-.2 2.8 0 5 2.6 5 5.8v.7h-5.2" />
    </>
  ),
  me: (
    <>
      <circle cx="12" cy="8" r="4.5" />
      <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8z" />
    </>
  ),
  bell: (
    <>
      <path d="M5 17.5h14l-1.8-2.6V10a5.2 5.2 0 0 0-10.4 0v4.9z" />
      <path d="M9.8 20.5h4.4" fill="none" />
    </>
  ),
  plus: <path d="M12 4v16M4 12h16" fill="none" />,
  chevron: <path d="m9 5 7 7-7 7" fill="none" />,
  copy: (
    <>
      <path d="M8.5 8.5h11v12h-11z" />
      <path d="M5.5 15.5h-2v-12h11v2" fill="none" />
    </>
  ),
  share: (
    <>
      <path d="M12 3v12M7.5 7.5 12 3l4.5 4.5" fill="none" />
      <path d="M5 11.5v9h14v-9" fill="none" />
    </>
  ),
  gear: (
    <>
      <path d="m10 2.8h4l.6 2.6 2.3 1.3 2.5-.8 2 3.4-1.9 1.9v2.6l1.9 1.9-2 3.4-2.5-.8-2.3 1.3-.6 2.6h-4l-.6-2.6-2.3-1.3-2.5.8-2-3.4 1.9-1.9v-2.6L2.6 9.3l2-3.4 2.5.8 2.3-1.3z" />
      <circle cx="12" cy="12" r="3" fill="none" />
    </>
  ),
  trophy: (
    <>
      <path d="M7 3.5h10v5a5 5 0 0 1-10 0z" />
      <path d="M7 5.5H3.5c0 3 1.5 4.8 3.8 5M17 5.5h3.5c0 3-1.5 4.8-3.8 5M12 13.5v4M8 20.5h8v-3H8z" fill="none" />
    </>
  ),
  flask: (
    <>
      <path d="M9.5 3.5h5M10.3 3.5v6L4.5 19.3a1.2 1.2 0 0 0 1 1.7h13a1.2 1.2 0 0 0 1-1.7l-5.8-9.8v-6" />
      <path d="M7.5 15h9" fill="none" />
    </>
  ),
  calendar: (
    <>
      <path d="M3.5 5.5h17v15h-17z" />
      <path d="M3.5 10h17M8 3v5M16 3v5" fill="none" />
    </>
  ),
  whistle: (
    <>
      <path d="M4 12.5a5.5 5.5 0 1 0 11 0V10h6V6.5H9.5A5.5 5.5 0 0 0 4 12.5z" />
      <circle cx="9.5" cy="12.5" r="1.6" fill="none" />
    </>
  ),
  camera: (
    <>
      <path d="M3 7.5h4l1.8-3h6.4l1.8 3h4v12H3z" />
      <circle cx="12" cy="13" r="3.6" fill="none" />
    </>
  ),
  card: (
    <>
      <path d="M5 2.5h14v19H5z" />
      <path d="M8 16.5h8M8 19h5" fill="none" />
      <circle cx="12" cy="9.5" r="3" fill="none" />
    </>
  ),
  bolt: <path d="M13.5 2.5 4.5 13.5h6l-1 8 9-11h-6z" />,
  check: <path d="m4.5 12.5 5 5 10-11" fill="none" />,
  close: <path d="m5.5 5.5 13 13M18.5 5.5l-13 13" fill="none" />,
};

export interface IconProps {
  name: IconName;
  size?: number;
  /** Flat fill for the closed shapes (sticker look). Default: none. */
  fill?: string;
  strokeWidth?: number;
  label?: string;
  className?: string;
}

export function Icon({ name, size = 24, fill = 'none', strokeWidth = 2.4, label, className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill={fill}
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinejoin="round"
      strokeLinecap="round"
      className={className}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
